import { DetailedResourceNodeFieldsFragment } from '@api/gql/graphql';
import { getFragmentData } from '@api/gql';
import { OWNERS_FIELD_FRAGMENT, PATH_FIELD_FRAGMENT, STATUS_FIELD_FRAGMENT } from '@Inventory/services/gql';
import { INTEGRATION_FRAGMENT, RESOURCE_TYPE_FRAGMENT } from '@AccessRightSizing/services/gql';
import { Paper } from '@mui/material';
import { DrawerDetail } from '@common/ui/DrawerDetail/DrawerDetail';
import { StatusColumn } from '@components';
import PrimarySecondary from '@common/ui/PrimarySecondary';
import { ResourceOwnersChips } from '@common/components/ResourceOwnersChips';
import { buildResourcePath } from '@common/utils/helpers';
import { riskLevelFields_Fragment } from '@AccessAlerts/services/gql';
import { RiskLevelImage } from '@common/components/RiskLevelImage';
import { useFlagFixed } from '@hooks';
import { Flag } from '@utils';

export function ResourceDetailsTable({ resource }: { resource: DetailedResourceNodeFieldsFragment }) {
  const { isEnabled: isRiskScoreEnabled } = useFlagFixed(Flag.SHOW_RISK_SCORE);

  const statusData = getFragmentData(STATUS_FIELD_FRAGMENT, resource.status);
  const resourceTypeData = getFragmentData(RESOURCE_TYPE_FRAGMENT, resource.type);
  const integrationData = getFragmentData(INTEGRATION_FRAGMENT, resource.integration);
  const ownersData = getFragmentData(OWNERS_FIELD_FRAGMENT, resource.owners);
  const pathData = getFragmentData(PATH_FIELD_FRAGMENT, resource.path);
  const riskLevel = getFragmentData(riskLevelFields_Fragment, resource.riskLevel);
  const pathKeysToDisplay = ['region', 'account'];
  const { literalPath, logicalPath } = buildResourcePath([...pathData]);

  return (
    <Paper>
      <DrawerDetail
        label="Name"
        value={<PrimarySecondary primary={resource.name} secondary={`(ID - ${resource?.id})`} />}
      />
      {integrationData && (
        <DrawerDetail
          label="Integration"
          value={
            <PrimarySecondary
              primary={integrationData.name}
              icon={integrationData.icons.svg}
              secondary={`(${integrationData.typeDisplayName})`}
            />
          }
        />
      )}
      {resourceTypeData && (
        <DrawerDetail
          label="Type"
          value={<PrimarySecondary primary={resourceTypeData.typeDisplayName} icon={resourceTypeData.icons.svg} />}
        />
      )}
      {isRiskScoreEnabled && riskLevel && (
        <DrawerDetail label="Risk Level" value={<RiskLevelImage riskLevel={riskLevel.riskLevel} />} />
      )}
      {pathData && (
        <DrawerDetail label="Path" value={<PrimarySecondary primary={literalPath} secondary={logicalPath} />} />
      )}
      {ownersData && <DrawerDetail label="Owners" value={<ResourceOwnersChips owners={[...ownersData]} />} />}
      {pathData?.map(
        (path) =>
          pathKeysToDisplay.includes(path.key) && (
            <DrawerDetail key={path.key} label={path.keyDisplayName} value={path.value} />
          ),
      )}
      <DrawerDetail label="Status" value={<StatusColumn status={statusData.status} />} />
    </Paper>
  );
}
